import React from 'react';
import { Typography, Table, Button, Tooltip, Badge, Space, Popconfirm, Checkbox } from 'antd';
import { formatPrice } from '~/Utils';
import { EditOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';

const { Text } = Typography;


export default function DetailsTable({ 
  items,
  loading,
  t,
  viewByService,
  approvedBy,
  completeEstimate,
  removeItems,        
  handleEdit,          
  handleAddSubVendors, 
 }) {
  
    const tableColumnsDetails = [
    {
      title: t('screens:estimate.id'),
      dataIndex: 'id',
      key: 'id',
      ellipsis: {
        showTitle: false,
      },
      align: 'center',
      width: '90px',
    },
    {
      title: t('screens:estimate.description'),
      key: 'estimateDescription',
      sorter: (a, b) => a.estimateDescription.description.length - b.estimateDescription.description.length,
      sortDirections: ['descend', 'ascend'],
      defaultSortOrder: 'ascend',
      ellipsis: {
        showTitle: false,
      },
      render: record => <>{record.estimateDescription.description}</>,
    },
    {
      title: t('screens:estimate.area'),
      key: 'area.name',
      sorter: (a, b) => a.area.name.length - b.area.name.length,
      ellipsis: {
        showTitle: false,
      },
      render: record => <>{record.area.name}</>,
    },
    {
      title: t('screens:estimate.laborGroup'),
      key: 'laborType.name',
      sorter: (a, b) => a.laborType.name.length - b.laborType.name.length,
      ellipsis: {
        showTitle: false,
      },
      render: record => {
        // show subvendors count in labor group
        const subVendorsNumber = completeEstimate?.find(labor => labor?.id === record?.laborType?.id)?.subVendors?.length;
        return (
          <>
            <Tooltip title="Manage service type subcontractors" placement="bottom">
              <Badge
                count={subVendorsNumber > 0 ? subVendorsNumber : 0}
                offset={[-5, 10]}
                color="#0093DD"
                showZero={false}
                size="small"
                overflowCount={99}
              >
                <Button
                  disabled={approvedBy !== null}
                  type="link"
                  icon={<TeamOutlined style={{ color: '#0093DD' }} />}
                  onClick={() => handleAddSubVendors(record.laborType.id)}
                />
              </Badge>
              <span style={{ marginLeft: '8px' }}>{record.laborType.name}</span>
            </Tooltip>
          </>
        );
      },
    },
    {
      title: t('screens:estimate.unit'),
      key: 'estimateDescription.unit',
      ellipsis: {
        showTitle: false,
      },
      width: '80px',
      render: record => <>{record.estimateDescription.unit}</>,
    },
    {
      title: t('screens:estimate.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
      ellipsis: {
        showTitle: false,
      },
      width: '80px',
    },
    {
      title: t('screens:estimate.copyQtdToVendor'),
      dataIndex: 'copyQtdToVendor',
      key: 'copyQtdToVendor',
      ellipsis: {
        showTitle: false,
      },
      width: '80px',
      render: copyQtdToVendor => <Checkbox checked={copyQtdToVendor} disabled={true} />,
    },
    {
      title: t('screens:estimate.materialValue'),
      dataIndex: 'materialValue',
      key: 'materialValue',
      sorter: (a, b) => a.materialValue - b.materialValue,
      ellipsis: {
        showTitle: false,
      },
      width: '100px',
      render: materialValue => <>{formatPrice(materialValue)}</>,
    },
    {
      title: t('screens:estimate.laborValue'),
      dataIndex: 'laborValue',
      key: 'laborValue',
      sorter: (a, b) => a.laborValue - b.laborValue,
      ellipsis: {
        showTitle: false,
      },
      width: '100px',
      render: laborValue => <>{formatPrice(parseFloat(laborValue))}</>,
    },
    {
      title: t('screens:estimate.totalMaterial'),
      key: 'total_material',
      sorter: (a, b) => parseFloat(a.materialValue * a.quantity) - parseFloat(b.materialValue * b.quantity),
      ellipsis: {
        showTitle: false,
      },
      width: '100px',
      render: record => <>{formatPrice(parseFloat(record.materialValue * record.quantity))}</>,
    },
    {
      title: t('screens:estimate.totalLabor'),
      key: 'total_labor',
      sorter: (a, b) => parseFloat(a.laborValue * a.quantity) - parseFloat(b.laborValue * b.quantity),
      ellipsis: {
        showTitle: false,
      },
      width: '100px',
      render: record => <>{formatPrice(parseFloat(record.laborValue * record.quantity))}</>,
    },
    {
      title: t('screens:estimate.totalMaterialAndLabor'),
      key: 'total_material_and_labor',
      sorter: (a, b) =>
        parseFloat((a.laborValue + a.materialValue) * a.quantity) -
        parseFloat((b.laborValue + b.materialValue) * b.quantity),
      ellipsis: {
        showTitle: false,
      },
      width: '100px',
      render: record => <>{formatPrice(parseFloat((record.laborValue + record.materialValue) * record.quantity))}</>,
    },
    {
      title: t('screens:estimate.data.actions'),
      key: 'actions',
      align: 'center',
      width: '90px',
      render: record => (
        <Space>
          <Tooltip title={t('screens:estimate.editItem')}>
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            icon={false}
            title={t('messages:confirmDelete')}
            onConfirm={() => removeItems(item => item.id === record.id)}
            okText={t('messages:yes')}
            cancelText={t('messages:no')}
          >
            <Tooltip title={t('screens:estimate.deleteItem')} placement="bottom">
              <Button type="link" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(column => {
    if (!viewByService && column.key === 'area.name') {
      return false;
    }
    if (viewByService && column.key === 'laborType.name') {
      return false;
    }
    if (approvedBy !== null && column.key === 'actions') {
      return false;
    }
    return true;
  });

  return (
    <Table
      bordered
      rowKey="id"
      pagination={false}
      loading={loading}
      columns={tableColumnsDetails}
      dataSource={items}
      showHeader={true}
      size="small"
      title={() => 'Details'}
      summary={pageData => {
        let totalMaterial = 0;
        let totalLabor = 0;
        let total = 0;
        pageData.forEach(({ quantity, materialValue, laborValue }) => {
          totalMaterial += materialValue * quantity;
          totalLabor += laborValue * quantity;
          total = totalMaterial + totalLabor;
        });
        return (
          <>
            <Table.Summary.Row style={{ backgroundColor: '#fffff0' }}>
              <Table.Summary.Cell index={0} colSpan={6} />
              <Table.Summary.Cell index={1}>
                <Text strong>Totals</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}>
                <Text strong>{formatPrice(totalMaterial)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3}>
                <Text strong>{formatPrice(totalLabor)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4}>
                <Text strong>{formatPrice(total)}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </>
        );
      }}
    />
  );
}
